/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';

import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import AutoSendToNewMatches from 'bundles/enterprise-admin-messaging-v2/components/common/AutoSendToNewMatches';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  draft: AIMessageDraft;
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
  onDone: () => void;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  supportText: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

/**
 * Final confirmation screen. Confirms the send count and lets the admin
 * opt in to auto-sending the same message to new learners that match this
 * audience in the future, on a selectable cadence.
 */
const MessageSentScreen = ({ draft, updateDraft, onDone }: Props) => {
  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          <Typography2 component="p" variant="bodyPrimary" css={styles.supportText}>
            <FormattedMessage
              message={_t('Your message has been sent to {count, plural, one {# learner} other {# learners}}.')}
              count={draft.audienceCount}
            />
          </Typography2>

          <AutoSendToNewMatches
            enabled={draft.autoSendEnabled}
            cadence={draft.autoSendCadence}
            onEnabledChange={(autoSendEnabled) => updateDraft({ autoSendEnabled })}
            onCadenceChange={(autoSendCadence) => updateDraft({ autoSendCadence })}
          />
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button variant="primary" onClick={onDone} data-testid="ai-message-modal-done">
          {_t('Done')}
        </Button>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default MessageSentScreen;
